import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * ポップオーバーを開いている間、その外側のスクロールを止めるフック。
 *
 * position: fixed で開いた瞬間の座標に固定表示するポップオーバー (テイク履歴等) は、
 * 背景がスクロールするとトリガーとの位置関係がずれてしまう。body の overflow を
 * 触る useLockBodyScroll では内側のスクロールコンテナ (シーン一覧など) を止められない
 * ため、wheel / touchmove を捕捉して既定動作ごと抑止する。
 *
 * ポップオーバー内部のスクロールは維持し、端に達したときの背景への伝播
 * (スクロールチェーン) だけを止める。
 *
 * @param active ロックを有効にするか (ポップオーバーの open 状態)
 * @param contentRef 内部スクロールを許可する要素 (ポップオーバー本体)
 */
export function useLockScrollOutside(
  active: boolean,
  contentRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!active) return;

    const handler = (e: WheelEvent | TouchEvent) => {
      const el = contentRef.current;
      const target = e.target as Node | null;

      if (el && target && el.contains(target)) {
        const scrollable = el.scrollHeight > el.clientHeight;
        if (!scrollable) {
          // スクロールできない中身なら、そのまま背景へ伝播してしまうので止める
          e.preventDefault();
          return;
        }
        // touchmove は方向が取れないので overscroll-behavior: contain に任せる
        if (!('deltaY' in e)) return;
        const atTop = el.scrollTop <= 0;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
        if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
          e.preventDefault();
        }
        return;
      }

      e.preventDefault();
    };

    window.addEventListener('wheel', handler, { passive: false, capture: true });
    window.addEventListener('touchmove', handler, { passive: false, capture: true });
    return () => {
      window.removeEventListener('wheel', handler, true);
      window.removeEventListener('touchmove', handler, true);
    };
  }, [active, contentRef]);
}
